'use client';
import Link from "next/link";
import {useRouter} from "next/navigation";
import {ComponentProps, Ref, useCallback, useEffect, useRef} from "react";

type RouterPrefetchOptions = NonNullable<Parameters<ReturnType<typeof useRouter>['prefetch']>[1]>;

// Wert von Nexts PrefetchKind.FULL. Der Enum liegt nur unter next/dist (intern),
// deshalb der String mit Typ-Cast statt des Imports.
const FULL_PREFETCH = {kind: 'full'} as unknown as RouterPrefetchOptions;

type HoverPrefetchLinkProps = ComponentProps<typeof Link> & {
    /**
     * Für Links auf Kategorieseiten. Lädt die komplette Zielseite vor (inkl.
     * dynamischer Inhalte wie H1 und Produktraster), auf Touch-Geräten zusätzlich
     * schon, wenn der Link ruhig im Bild ist (mit striktem Budget, siehe unten).
     *
     * Warum: Von einer Kategorieseite aus lädt Next mit dem Standard-Prefetch
     * keine andere Kategorieseite vor, weil die statische Hülle der Route schon im
     * Cache liegt und der dynamische Teil nie vorgeladen wird (gemessen
     * 24.09.2026: 0 Requests). Mit FULL ist es 1 Request und der Klick zeigt den
     * Inhalt sofort.
     */
    eagerPrefetch?: boolean;
};

// ---------------------------------------------------------------------------
// Sichtbarkeits-Prefetch für Touch-Geräte (nur eagerPrefetch-Links)
// ---------------------------------------------------------------------------
// Ungebremst löste das auf einer Kategorieseite 59 volle Prefetches (3,4 MB) aus,
// fast alle ungenutzt, und verlangsamte echte Taps. Deshalb:
// - höchstens VISIBLE_BUDGET Prefetches pro Seite,
// - nur Links, die VISIBLE_DWELL_MS ruhig sichtbar sind (nicht während Scrollens),
// - nacheinander mit Abstand, nie parallel,
// - kein Prefetch bei Datensparmodus, langsamem Netz oder verstecktem Tab,
// - jede Berührung pausiert die Warteschlange kurz (echter Tap hat Vorrang).
const VISIBLE_BUDGET = 4;
const VISIBLE_DWELL_MS = 500;
const SCROLL_IDLE_MS = 300;
const QUEUE_GAP_MS = 400;

type Candidate = { href: string; run: (href: string) => void };

const visible = {
    path: '',
    used: 0,
    lastScroll: 0,
    queue: [] as Candidate[],
    timer: null as ReturnType<typeof setTimeout> | null,
    listening: false,
};

function touchDeviceAllowsPrefetch(): boolean {
    if (typeof window === 'undefined') return false;
    if (!window.matchMedia('(hover: none)').matches) return false;
    if (document.hidden) return false;
    const conn = (navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string }
    }).connection;
    if (conn?.saveData) return false;
    if (conn?.effectiveType && ['slow-2g', '2g', '3g'].includes(conn.effectiveType)) return false;
    return true;
}

function resetForNewPage() {
    if (visible.path !== location.pathname) {
        visible.path = location.pathname;
        visible.used = 0;
        visible.queue = [];
    }
}

function ensureGlobalListeners() {
    if (visible.listening) return;
    visible.listening = true;
    window.addEventListener('scroll', () => {
        visible.lastScroll = Date.now();
    }, {passive: true});
    // Jede Berührung (Scroll-Geste oder Tap) pausiert die Warteschlange kurz,
    // damit ein echter Tap nicht mit Hintergrund-Prefetches um die Leitung kämpft.
    // Nach einem Tap wechselt die Seite und resetForNewPage() leert die Schlange.
    window.addEventListener('touchstart', () => {
        visible.lastScroll = Date.now() + 300;
    }, {passive: true, capture: true});
}

function pumpQueue() {
    if (visible.timer) return;
    const next = () => {
        visible.timer = null;
        resetForNewPage();
        if (visible.queue.length === 0 || visible.used >= VISIBLE_BUDGET) return;
        const sinceScroll = Date.now() - visible.lastScroll;
        if (sinceScroll < SCROLL_IDLE_MS) {
            visible.timer = setTimeout(next, SCROLL_IDLE_MS - sinceScroll);
            return;
        }
        const candidate = visible.queue.shift()!;
        visible.used += 1;
        candidate.run(candidate.href);
        visible.timer = setTimeout(next, QUEUE_GAP_MS);
    };
    visible.timer = setTimeout(next, 0);
}

let sharedObserver: IntersectionObserver | null = null;
const observed = new Map<Element, { href: string; run: (href: string) => void; dwell: ReturnType<typeof setTimeout> | null }>();

function observe(el: Element, href: string, run: (href: string) => void) {
    if (!sharedObserver) {
        sharedObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const item = observed.get(entry.target);
                if (!item) continue;
                if (entry.isIntersecting) {
                    if (item.dwell) continue;
                    item.dwell = setTimeout(() => {
                        item.dwell = null;
                        resetForNewPage();
                        if (visible.used >= VISIBLE_BUDGET || !touchDeviceAllowsPrefetch()) return;
                        unobserve(entry.target);
                        visible.queue.push({href: item.href, run: item.run});
                        pumpQueue();
                    }, VISIBLE_DWELL_MS);
                } else if (item.dwell) {
                    clearTimeout(item.dwell);
                    item.dwell = null;
                }
            }
        });
    }
    observed.set(el, {href, run, dwell: null});
    sharedObserver.observe(el);
}

function unobserve(el: Element) {
    const item = observed.get(el);
    if (item?.dwell) clearTimeout(item.dwell);
    observed.delete(el);
    sharedObserver?.unobserve(el);
}

/**
 * next/link mit Intent-basiertem Prefetch: kein Viewport-Prefetch (Lighthouse-
 * neutral, kein Request-Spam beim Seitenstart), stattdessen lädt die Zielseite
 * bei Hover/Touch über die imperative router.prefetch()-API vor.
 *
 * Bewusst NICHT das offizielle Doku-Muster (prefetch-Prop bei Hover von false
 * auf true flippen): das ist in Next 16.2.3 mit cacheComponents kaputt — der
 * State-Flip führt nicht zur Registrierung des Links beim Prefetch-Scheduler,
 * es wird nie gefetcht (empirisch verifiziert, 2026-06-11). router.prefetch()
 * umgeht die Link-interne Visibility-Maschinerie und feuert direkt.
 *
 * Prefetch greift nur im Production-Build (npm start), nie im Dev-Server.
 */
export default function HoverPrefetchLink({onMouseEnter, onMouseLeave, onTouchStart, href, eagerPrefetch = false, ref: forwardedRef, ...props}: HoverPrefetchLinkProps) {
    const router = useRouter();
    const prefetchedHref = useRef<string | null>(null);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const anchor = useRef<HTMLAnchorElement | null>(null);
    // Eigene Ref (Sichtbarkeit) mit der von außen kommenden Ref zusammenführen,
    // z. B. Radix NavigationMenuLink asChild im Mega-Menü (Fokus/Tastatur).
    const setAnchor = useCallback((node: HTMLAnchorElement | null) => {
        anchor.current = node;
        const outer = forwardedRef as Ref<HTMLAnchorElement> | undefined;
        if (typeof outer === 'function') outer(node);
        else if (outer && typeof outer === 'object') (outer as { current: HTMLAnchorElement | null }).current = node;
    }, [forwardedRef]);
    const hrefString = typeof href === 'string' ? href : null;

    const prefetchNow = (target: string | null = hrefString) => {
        if (!target || prefetchedHref.current === target) return;
        prefetchedHref.current = target;
        if (eagerPrefetch) router.prefetch(target, FULL_PREFETCH);
        else router.prefetch(target);
    };

    useEffect(() => {
        const el = anchor.current;
        if (!eagerPrefetch || !el || !hrefString) return;
        if (!touchDeviceAllowsPrefetch()) return;
        ensureGlobalListeners();
        observe(el, hrefString, (target) => prefetchNow(target));
        return () => unobserve(el);
    }, [eagerPrefetch, hrefString]);

    useEffect(() => () => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
    }, []);

    return (
        <Link
            {...props}
            ref={setAnchor}
            href={href}
            prefetch={false}
            onMouseEnter={(e) => {
                // Volle Prefetches erst nach kurzem Verweilen, damit ein Mauszug
                // quer über viele Links nicht jeden davon rendern lässt.
                if (eagerPrefetch) {
                    hoverTimer.current = setTimeout(() => prefetchNow(), 80);
                } else {
                    prefetchNow();
                }
                onMouseEnter?.(e);
            }}
            onMouseLeave={(e) => {
                if (hoverTimer.current) clearTimeout(hoverTimer.current);
                onMouseLeave?.(e);
            }}
            onTouchStart={(e) => {
                prefetchNow();
                onTouchStart?.(e);
            }}
        />
    );
}
